// @ts-ignore
import { type Runtime } from "@observablehq/runtime";

import {
	type JsxParam,
	type JsxSimpleObjectArrayParam,
	type JsxStringUnionParam,
} from "~/runtime/cell-compiler/analysis/JsxParameterInfo";

import { CellResultsObserver } from "./CellResultsObserver";
import { Variable } from "./Variable";
import { type ObservableModule, type ResultsObserver } from "./interfaces";

export interface Snippet {
	fieldType: JsxStringUnionParam | JsxSimpleObjectArrayParam | JsxParam;
	id: string;
	expression: string;
	emittedCode: string;
	staticError?: Error;
	refs: string[];
}

/* CellHandles represent the binding between the Observable Runtime, and the moment Cell. A CellHandle
 * contains the knowledge of a cells name binding (an Observable Variable) and any imports done by
 * the cell.
 */

export class SnippetHandle<T> {
	private resultsObserver: CellResultsObserver;
	public variable: Variable;

	constructor(
		public fieldCode: Snippet,
		private runtime: Runtime,
		private module: ObservableModule
	) {
		this.resultsObserver = new CellResultsObserver();
		this.variable = new Variable(module, this.resultsObserver);
		this.defineVariables();
	}

	differentFrom(other: Snippet) {
		return (
			this.fieldCode.emittedCode !== other.emittedCode ||
			this.fieldCode.fieldType !== other.fieldType
		);
	}

	dispose() {
		this.variable.delete();
		this.resultsObserver.resetStaticError();
	}

	/* Warning! Only one subscriber is allowed at a time. Calling this function
	 * with a new subscriber will destroy the previous binding.
	 */
	setSubscriber(observer: ResultsObserver) {
		this.resultsObserver.setSubscriber(observer);
	}

	update(fieldCode: Snippet): SnippetHandle<T> {
		const cellHandle = new SnippetHandle(fieldCode, this.runtime, this.module);
		cellHandle.resultsObserver = this.resultsObserver;
		cellHandle.variable = this.variable;

		cellHandle.dispose();
		cellHandle.defineVariables();
		return cellHandle;
	}

	private defineVariables() {
		try {
			const { refs } = this.fieldCode;
			const f = Function(`return ${this.fieldCode.emittedCode}`)();
			this.variable.define({
				inputs: refs,
				definition: f,
			});
		} catch (e) {
			this.variable.define({
				definition: () => {
					throw e;
				},
			});
		}
	}
}
